import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Lock, ChevronDown, X, BookOpen, Languages, Volume2 } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "@/lib/toast";

const VIDEO_LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "te", label: "తెలుగు (Telugu)" },
  { code: "ta", label: "தமிழ் (Tamil)" },
  { code: "kn", label: "ಕನ್ನಡ (Kannada)" },
  { code: "ml", label: "മലയാളം (Malayalam)" },
  { code: "nl", label: "Nederlands (Dutch)" },
];

/* Article summary video + on-demand translation (subtitles + spoken voice-over). */
function ArticleVideo({ fileId, caption }) {
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);
  const [tr, setTr] = useState(null); // { text, audio_file_id }

  const changeLang = async (code) => {
    setLang(code);
    setTr(null);
    if (code === "en" || !caption) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/narrate", { text: caption, lang: code });
      setTr({ text: data.text, audio_file_id: data.audio_file_id });
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || "Could not translate the summary right now.");
    } finally { setBusy(false); }
  };

  return (
    <div className="art-video" data-testid="article-video">
      <video controls preload="metadata" style={{ width: "100%", borderRadius: 12, background: "#000", maxHeight: 380 }}
        src={`${API}/public/article-video/${fileId}`} data-testid="article-video-player">
        Your browser does not support video playback.
      </video>

      {caption && (
        <>
          <div className="art-video-trbar">
            <span className="art-video-trlabel"><Languages size={14} /> Watch summary in</span>
            <div className="jr-spec-select" style={{ minWidth: 180 }}>
              <select value={lang} onChange={e => changeLang(e.target.value)} data-testid="article-video-lang">
                {VIDEO_LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              <ChevronDown size={16} />
            </div>
          </div>

          {busy && <p className="lead" style={{ fontSize: 13, marginTop: 8 }} data-testid="article-video-translating">Translating the summary…</p>}

          {lang === "en" ? (
            <p className="art-video-caption" data-testid="article-video-caption">{caption}</p>
          ) : tr && (
            <div data-testid="article-video-translated">
              {tr.audio_file_id && (
                <div style={{ margin: "10px 0 6px" }}>
                  <span className="art-video-trlabel" style={{ marginBottom: 6, display: "inline-flex" }}><Volume2 size={14} /> Listen in this language</span>
                  <audio controls src={`${API}/public/tts/${tr.audio_file_id}`} style={{ width: "100%" }} data-testid="article-video-audio" />
                </div>
              )}
              <p className="art-video-caption" data-testid="article-video-caption-translated">{tr.text}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Journal() {
  const nav = useNavigate();
  const [c, setC] = useState({});
  const [groups, setGroups] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [specialty, setSpecialty] = useState("");
  const [reading, setReading] = useState(null); // {title, body} | "loading"

  useEffect(() => {
    api.get("/public/content").then(r => setC(r.data.content || {})).catch(() => {});
    api.get("/public/articles").then(r => {
      const g = r.data.groups || [];
      setGroups(g);
      setIsPremium(!!r.data.is_premium);
      const firstWithArticles = g.find(x => x.count > 0);
      setSpecialty((firstWithArticles || g[0] || {}).specialty || "");
    }).catch(() => {});
  }, []);

  const current = groups.find(g => g.specialty === specialty) || { articles: [] };

  const openArticle = async (a) => {
    if (a.locked) { toast("This article is for Premium subscribers. Log in and upgrade to read all articles."); return; }
    setReading("loading");
    try {
      const { data } = await api.get(`/articles/${a.id}`);
      setReading(data.article);
    } catch (e) {
      const code = e.response?.status;
      if (code === 401) { toast("Please log in as a patient to read articles."); nav("/patient"); }
      else if (code === 403) toast("This article is for Premium subscribers. Upgrade to read it.");
      else toast(formatApiError(e.response?.data?.detail) || "Could not open this article.");
      setReading(null);
    }
  };

  return (
    <section className="pgwrap">
      <span className="pg-eyebrow">The Journal</span>
      <h2 className="pg-h2">Article of the month</h2>
      <div className="jr-feature" style={{ marginBottom: 14 }} data-testid="journal-article-of-month">
        <div className="body">
          <h3>{c.article_title || "Health, explained without the jargon"}</h3>
          <p>{c.article_excerpt || "This space features a new article each month, published by our specialists."}</p>
        </div>
        <div className="art"><FileText size={40} /></div>
      </div>
      {c.article_video_id && (
        <div className="jr-aom-video" style={{ marginBottom: 24 }}>
          <ArticleVideo fileId={c.article_video_id} caption={c.article_video_caption || c.article_excerpt || ""} />
        </div>
      )}

      <div className="jr-more-head">
        <h2 className="pg-h2" style={{ fontSize: 24, margin: 0 }}>More from the Journal</h2>
        <div className="jr-spec-select" data-testid="journal-specialty-select">
          <select value={specialty} onChange={e => setSpecialty(e.target.value)} data-testid="journal-specialty">
            {groups.map(g => (
              <option key={g.specialty} value={g.specialty}>{g.specialty}{g.count ? ` (${g.count})` : ""}</option>
            ))}
          </select>
          <ChevronDown size={16} />
        </div>
      </div>

      {!isPremium && current.articles.length > 1 && (
        <p className="jr-tier-note" data-testid="journal-tier-note">
          <Lock size={13} /> Regular members can read the first article in each specialty. <b>Premium subscribers</b> read them all.
        </p>
      )}

      <div className="grid-auto">
        {current.articles.length === 0 && (
          <div className="card2 topic" data-testid="journal-empty">
            <h4>No articles yet</h4>
            <p>Our specialists are preparing articles for {specialty}. Check back soon.</p>
          </div>
        )}
        {current.articles.map((a) => (
          <div className={`card2 topic ${a.locked ? "locked" : ""}`} key={a.id}
            onClick={() => openArticle(a)} data-testid={`journal-article-${a.id}`}>
            <span className="k">{specialty}{a.locked ? " · Premium" : ""}</span>
            <h4>{a.title} {a.locked && <Lock size={14} style={{ verticalAlign: "-2px" }} />}</h4>
            <p>{a.excerpt || "Read this article from our specialists."}</p>
            <span className="jr-read">
              <BookOpen size={13} /> {a.locked ? "Premium" : "Read article"}
              {a.has_video && <span className="jr-video-chip"><Volume2 size={11} /> Video</span>}
            </span>
          </div>
        ))}
      </div>

      {reading && (
        <div className="modal-bg" onClick={() => setReading(null)} data-testid="article-reader">
          <div className="modal article-reader-card" onClick={e => e.stopPropagation()}>
            <button className="m-close" onClick={() => setReading(null)} data-testid="article-reader-close"><X size={18} /></button>
            {reading === "loading" ? (
              <p className="lead">Opening…</p>
            ) : (
              <>
                <span className="pg-eyebrow">{reading.specialty}</span>
                <h2 className="pg-h3" style={{ marginTop: 4 }} data-testid="article-reader-title">{reading.title}</h2>
                {reading.video_file_id && (
                  <div style={{ margin: "14px 0 18px" }}>
                    <ArticleVideo fileId={reading.video_file_id} caption={reading.caption || ""} />
                  </div>
                )}
                <div className="article-body" data-testid="article-reader-body">
                  {reading.body.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
